import { atom } from "jotai";
import { RouterProvider, createHashRouter, redirect } from "react-router-dom";
import Summary from "./routes/Summary";
import ThankYou from "./routes/ThankYou";
import SendFailed from "./routes/SendFailed";
import Root from "./routes/root";
import Intro from "./routes/Intro";

import { FormModel } from "./types/FormTypes";
import SurchargeNumber from "./routes/SurchargeNumber";
import TicketDetails from "./routes/TicketType";
import TicketWithApp from "./routes/TicketWithApp";
import TicketWithBlip from "./routes/TicketWithBlip";
import TicketWithTravelcard from "./routes/TicketWithTravelcard";
import Description from "./routes/Description";
import ContactDetails from "./routes/ContactDetails";
import AgeCheck from "./routes/AgeCheck";
import AppealForWho from "./routes/AppealForWho";
import TicketWithPaperTicket from "./routes/TicketWithPaperTicket";

require("./extensions/yup-extensions");

// Store - Collecting FormData
export const formDataAtom = atom<FormModel>({
  surchargenumber: "",
  phone: "",
  ticketType: "",
  travelcardnumber: "",
  ticketNumber: "",
  description: "",
  agecheck: "",
  appealforwho: "",
  reporterEmail: "",
  firstname: "",
  lastname: "",
  email: "",
});

// Flow guard and router setup
const FLOW_KEY = "appeal-surcharge-flow";

const navigationEntry = performance.getEntriesByType("navigation")[0] as
  | PerformanceNavigationTiming
  | undefined;

const isReload = navigationEntry?.type === "reload";

if (isReload) {
  sessionStorage.removeItem(FLOW_KEY);
}

function startFlow() {
  sessionStorage.setItem(FLOW_KEY, "active");
  return null;
}

function isFlowActive() {
  return sessionStorage.getItem(FLOW_KEY) === "active";
}

function clearFlow() {
  sessionStorage.removeItem(FLOW_KEY);
  return null;
}

function requireActiveFlow() {
  return isFlowActive() ? null : redirect("/");
}

function createProtectedStep(path: string, element: JSX.Element) {
  return {
    path,
    element,
    loader: async () => {
      return requireActiveFlow();
    },
  };
}

const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <Intro />,
        loader: () => {
          return startFlow();
        },
      },

      createProtectedStep("tillaggsavgiftsnummer", <SurchargeNumber />),
      createProtectedStep("biljettdetaljer", <TicketDetails />),
      createProtectedStep("biljett-med-app", <TicketWithApp />),
      createProtectedStep("biljett-med-resekort", <TicketWithTravelcard />),
      createProtectedStep("biljett-med-blipp", <TicketWithBlip />),
      createProtectedStep("pappersbiljett", <TicketWithPaperTicket />),
      createProtectedStep("vad-var-det-som-hande", <Description />),
      createProtectedStep("ar-du-over-18", <AgeCheck />),
      createProtectedStep("vem-overklagar-du-for", <AppealForWho />),
      createProtectedStep("kontaktuppgifter", <ContactDetails />),
      createProtectedStep("sammanfattning", <Summary />),

      {
        path: "tack",
        element: <ThankYou />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
      {
        path: "ett-fel-intraffade",
        element: <SendFailed />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
    ],
  },
]);

// Count pages
const pagePaths =
  router?.routes[0]?.children?.map((child) => child.path).filter(Boolean) || [];

export const pageCountAtom = atom({
  pagePaths,
});

function App() {
  return <RouterProvider router={router} />;
}

export default App;
